import { Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate, useParams } from "react-router";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useClientPermissionsContext } from "@entrilia/shared/contexts/ClientPermissionsContext";
import biApi from "../../../../../../api/biApi";
import useClientContext from "../../../../../../context/useClientContext.ts";
import {
  getEditReportTemplateUrl,
  getOrganizationReportTemplatesUrl,
} from "../../../report-lists/reports/utilities/editReportUrl.ts";
import { getAccessibleCompanies } from "../../../report-lists/templates/helpers/reportTemplateHelper.ts";
import { useBiClientContext } from "../../contexts/ClientContext";
import { selectCurrentReportType, selectHasCurrentReport } from "../../store/selectors/currentReportSelectors";
import type { AppDispatch } from "../../store/store";
import { setEditReport } from "../../store/thunks/currentReportThunks";
import { createMetaDataAction, createReportTemplatesAction } from "../../store/utils/storeUtils";
import DrillDownEntry from "../builder/common/drillDown/DrillDownEntry";
import EditCommonDataProvider from "../builder/common/EditCommonDataProvider";
import TemplateNotFound from "./TemplateNotFound";

export default function TemplateRoot({ children }: React.PropsWithChildren) {
  const { id, organization } = useParams();
  const navigate = useNavigate();
  const { clientCode: dataSource } = useClientContext();
  const { clientInfo, clients } = useBiClientContext();
  const { permissionSets } = useClientPermissionsContext();
  const [showTemplateNotFound, setShowTemplateNotFound] = useState(false);
  const dispatch: AppDispatch = useDispatch();
  const templateReportType = useSelector(selectCurrentReportType);
  const hasCurrentReport = useSelector(selectHasCurrentReport);

  const companies = useMemo(
    () => getAccessibleCompanies(clients, permissionSets, clientInfo?.organization || ""),
    [clients, permissionSets, clientInfo?.organization]
  );

  useEffect(() => {
    dispatch(createReportTemplatesAction("setCompanies", companies));
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [companies]);

  useEffect(() => {
    if (id === undefined || organization === undefined) {
      return;
    }

    if (dataSource && companies.length > 0 && !companies.find((c) => c.clientCode === dataSource)) {
      const newDataSource = companies[0]?.clientCode || "";
      navigate(getEditReportTemplateUrl(organization, newDataSource, id));
      return;
    }

    biApi
      .getReportTemplateForOrganization({ templateId: id, organization })
      .then((resp) => {
        if (!resp.success || resp.data === undefined) {
          setShowTemplateNotFound(true);
        } else {
          dispatch(setEditReport(resp.data));
        }
      })
      .catch(() => setShowTemplateNotFound(true));

    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [id, organization, dataSource, companies]);

  useEffect(() => {
    if (templateReportType !== undefined) {
      dispatch(createMetaDataAction("updateReportType", templateReportType));
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [templateReportType]);

  if (id === undefined) {
    return <Navigate to={""} />;
  }

  if (!clientInfo || showTemplateNotFound === true || companies.length === 0) {
    return (
      <TemplateNotFound backUrl={getOrganizationReportTemplatesUrl(clientInfo?.organization || organization || "")} />
    );
  }

  if (!hasCurrentReport) {
    return <InlineLoader text="Loading metadata, it won't take long..." />;
  }

  return (
    <EditCommonDataProvider>
      <Grid container sx={{ bgcolor: "background.paper", width: "100%" }}>
        {children}
        <DrillDownEntry reportId={id} />
      </Grid>
    </EditCommonDataProvider>
  );
}
